import { Component, OnDestroy, OnInit } from '@angular/core';
import { config } from '@app/core/app-config';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { SubscriptionData, User } from '@app/shared/interfaces';
import { BillingService } from '@app/shared/services/billing/billing.service';
import { IntercomService } from '@app/shared/services/intercom.service';
import { ScriptLoaderService } from '@app/shared/services/script-loader/script-loader.service';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Article, Category, HomePage } from '../../models/support-documentation';
import { OverlaySimulatorService } from '../../services/overlay-simulator.service';
import { SupportDocumentationService } from '../../services/support-documentation.service';

declare global {
  interface Window {
    Headway: any;
  }
}

@Component({
  standalone: false,
  selector: 'sbnb-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  loading: boolean;
  categories: Category[];
  recent_articles: Article[];
  popular_articles: Article[];
  user: User;
  headwayUnseenCount: number;
  perms$ = this.userPermissionsService;
  Features = Features;

  config = config;

  showBookAnOnboardingCallAction?: boolean;
  private activeProperties?: number;
  private destroyed$ = new Subject<void>();

  constructor(
    public overlaySimulatorService: OverlaySimulatorService,
    private authService: AuthenticationService,
    private userPermissionsService: UserPermissionsService,
    private supportDocumentationService: SupportDocumentationService,
    private scriptLoader: ScriptLoaderService,
    private billingService: BillingService,
    private intercomService: IntercomService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.user = this.authService.getUserDetails();
    this.overlaySimulatorService.rememberCurrentUrl();

    this.supportDocumentationService.getHomePage().subscribe((homePage: HomePage) => {
      this.categories = homePage.categories;
      this.recent_articles = homePage.recent_articles;
      this.popular_articles = homePage.popular_articles;

      // Don't display getting started category
      if (this.categories[0]?.name === 'Getting started') {
        this.categories.shift();
      }

      this.loading = false;
    });

    this.userPermissionsService
      .isPrimaryUser()
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((isPrimaryUser) => {
        if (isPrimaryUser) {
          this.billingService.getSubscriptionDetails().subscribe((res: SubscriptionData) => {
            this.showBookAnOnboardingCallAction = res?.status === 'trialing';
            this.activeProperties = res?.active_properties;
          });
        }
      });
  }

  ngAfterViewInit(): void {
    this.loadHeadway();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  loadHeadway(): void {
    this.scriptLoader
      .load({
        name: 'Headway Widget',
        src: 'https://cdn.headwayapp.co/widget.js',
      })
      .subscribe(() => {
        (window as any).Headway.init({
          selector: '.headway',
          account: '7wmd5y',
          callbacks: {
            onWidgetReady: (widget) => {
              this.headwayUnseenCount = widget.getUnseenCount();
            },
          },
        });
      });
  }

  clearHeadwayUnseenCount(): void {
    if (!this.headwayUnseenCount) {
      return;
    }

    (window as any).Headway.markAllSeen();
    this.headwayUnseenCount = 0;
  }

  openIntercom() {
    this.intercomService.show('');
  }

  onboardingCall() {
    if (!this.activeProperties || !this.showBookAnOnboardingCallAction) return;

    if (this.activeProperties <= 2) {
      window.open(
        'https://meetings.hubspot.com/vkovacevic/ae-full-team-roundrobin-1-2?utm_campaign=OnboardingPage&utm_source=OnboardingPage&utm_medium=OnboardingPage',
        '_blank'
      );
    } else if (this.activeProperties >= 3 && this.activeProperties <= 40) {
      window.open(
        'https://meetings.hubspot.com/vkovacevic/ae-full-team-roundrobin-3-40?utm_campaign=OnboardingPage&utm_source=OnboardingPage&utm_medium=OnboardingPage',
        '_blank'
      );
    } else {
      window.open(
        'https://meetings.hubspot.com/ebekus/ae-enteprise-link-ewelina?utm_campaign=OnboardingPage&utm_source=OnboardingPage&utm_medium=OnboardingPage',
        '_blank'
      );
    }
  }
}
